import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DynamicSection from '../components/dynamic/DynamicSection';
import DynamicCard from '../components/dynamic/DynamicCard';
import DynamicTag from '../components/dynamic/DynamicTag';
import DynamicButton from '../components/dynamic/DynamicButton';
import { blogAPI } from '../services/api';
import { BlogPost } from '../types';
import { FaClock, FaCalendar, FaUser, FaArrowRight } from 'react-icons/fa';

const Blog: React.FC = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  useEffect(() => {
    const loadPosts = async () => {
      try {
        const response = await blogAPI.getAll();
        if (response && response.data) {
          setPosts(Array.isArray(response.data) ? response.data : []);
        } else {
          setPosts([]);
        }
      } catch (error: any) {
        console.error('Error loading blog posts:', error);
        setPosts([]);
      } finally {
        setLoading(false);
      }
    };
    loadPosts();
  }, []);

  const categories = posts.length > 0 
    ? ['all', ...Array.from(new Set(posts.map(p => p.category)))]
    : ['all'];
  const filteredPosts = selectedCategory === 'all' 
    ? posts 
    : posts.filter(p => p.category === selectedCategory);

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center bg-white dark:bg-gray-900">
        <div className="text-xl text-gray-600 dark:text-gray-400">Loading blog posts...</div>
      </div>
    );
  }

  return (
    <div className="pt-20 bg-white dark:bg-gray-900 min-h-screen">
      <DynamicSection
        title="Blog & Articles"
        subtitle="Insights, tutorials, and thoughts on software development"
      >
        <div className="mb-8 flex flex-wrap gap-2 justify-center">
          {categories.map((category) => (
            <DynamicButton
              key={category}
              variant={selectedCategory === category ? 'primary' : 'outline'}
              onClick={() => setSelectedCategory(category)}
              className="capitalize"
            >
              {category}
            </DynamicButton>
          ))}
        </div>

        {filteredPosts.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-600 dark:text-gray-400">No blog posts found.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPosts.map((post) => {
              // Generate a meaningful placeholder image as SVG data URI
              const getPlaceholderImage = () => {
                const category = post.category || 'Blog Post';
                const gradientId = `grad-${post.id}`;
                const svg = `
                  <svg width="800" height="400" xmlns="http://www.w3.org/2000/svg">
                    <defs>
                      <linearGradient id="${gradientId}" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" style="stop-color:#4F46E5;stop-opacity:1" />
                        <stop offset="100%" style="stop-color:#7C3AED;stop-opacity:1" />
                      </linearGradient>
                    </defs>
                    <rect width="800" height="400" fill="url(#${gradientId})"/>
                    <text x="50%" y="45%" font-family="Arial, sans-serif" font-size="32" font-weight="bold" 
                          fill="white" text-anchor="middle" dominant-baseline="middle">${category}</text>
                    <text x="50%" y="55%" font-family="Arial, sans-serif" font-size="18" 
                          fill="rgba(255,255,255,0.9)" text-anchor="middle" dominant-baseline="middle">Blog Article</text>
                  </svg>
                `.trim();
                // Use encodeURIComponent instead of btoa to handle Unicode characters properly
                return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
              };

              const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                const target = e.target as HTMLImageElement;
                target.src = getPlaceholderImage();
              };

              return (
                <DynamicCard key={post.id} className="overflow-hidden flex flex-col">
                  <img
                    src={post.imageUrl || getPlaceholderImage()}
                    alt={post.title}
                    className="w-full h-48 object-cover"
                    onError={handleImageError}
                  />
                  <div className="p-6 flex flex-col flex-grow">
                    <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400 mb-3">
                      <span className="flex items-center">
                        <FaUser className="mr-1" />
                        {post.author}
                      </span>
                      <span className="flex items-center">
                        <FaCalendar className="mr-1" />
                        {new Date(post.publishedAt).toLocaleDateString()}
                      </span>
                      <span className="flex items-center">
                        <FaClock className="mr-1" />
                        {post.readTime} min
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                      {post.title}
                    </h3>
                    <p className="text-gray-700 dark:text-gray-300 mb-4 flex-grow">
                      {post.excerpt}
                    </p>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {post.tags.slice(0, 3).map((tag) => (
                        <DynamicTag key={tag} variant="primary" size="sm">
                          {tag}
                        </DynamicTag>
                      ))}
                    </div>
                    <DynamicButton
                      variant="outline"
                      onClick={() => navigate(`/blog/${post.slug}`)}
                      className="w-full mt-auto"
                    >
                      Read More
                      <FaArrowRight className="ml-2 inline" />
                    </DynamicButton>
                  </div>
                </DynamicCard>
              );
            })}
          </div>
        )}
      </DynamicSection>
    </div>
  );
};

export default Blog;
